import { CdkDrag, CdkDragMove } from '@angular/cdk/drag-drop';
import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidAudioCard } from '../../components/rid-audio-card/rid-audio-card';
import { RidKlankvoetRuleGlyph } from '../../components/rid-klankvoet-rule-glyph/rid-klankvoet-rule-glyph';
import { RidKlankvoetRuleModal, TKlankvoetRule } from '../../components/rid-klankvoet-rule-modal/rid-klankvoet-rule-modal';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import { RidOverlayFeedback } from '../../components/rid-overlay-feedback/rid-overlay-feedback';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidSfxDirective } from '../../directives/rid-sfx.directive';
import { AudioEffectPlayer } from '../../utils/audio.utils';
import { TKlankvoetenAssignment } from '../rid-klankvoeten/rid-klankvoeten';

export type TKlankvoetenRecognizeAssignment = Pick<TKlankvoetenAssignment, 'word' | 'audio' | 'rule'>;

const MAX_ATTEMPTS = 2;

const RULES: TKlankvoetRule[] = ['SPLIT_AFTER_MULTITEKEN_KLANK', 'SPLIT_BEFORE_CENTRAL_CONSONANT', 'SPLIT_BETWEEN_CONSONANTS'];

@Component({
  selector: 'rid-klankvoeten-recognize',
  imports: [
    CdkDrag,
    RidMechanicBackground,
    RidMechanicWrapper,
    RidTopBar,
    RidMechanicContent,
    RidMechanicTitle,
    RidAudioCard,
    RidKlankvoetRuleGlyph,
    RidOverlayFeedback,
    RidModalBackground,
    RidKlankvoetRuleModal,
    RidSfxDirective,
  ],
  templateUrl: './rid-klankvoeten-recognize.html',
  host: {
    class: 'block',
  },
})
export class RidKlankvoetenRecognize {
  assignments = input.required<TKlankvoetenRecognizeAssignment[]>();

  protected readonly rules = RULES;

  index = signal(0);
  assignment = computed(() => this.assignments()[this.index()]);

  isDragging = signal(false);
  isEvaluating = signal(false);
  isCorrect = signal(false);
  // Wrong drops for the current word; the rule explanation only shows once these run out.
  attempts = signal(0);
  target = signal<TKlankvoetRule | undefined>(undefined);
  // The rule cards only appear once the first word has been heard, and then stay put for the rest of the words.
  areRulesVisible = signal(false);
  showExplanation = signal(false);

  draggable = viewChild.required<CdkDrag>('draggable');
  audioCard = viewChild.required<RidAudioCard>('audioCard');
  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  finished = output<void>();

  async onWordPoppedIn() {
    await this.audioCard().play();
    this.areRulesVisible.set(true);
  }

  onDragStarted() {
    this.isDragging.set(true);
  }

  // NOTE: this is needed for touch support, because the native touchenter and touchleave events don't work.
  onDragMoved(event: CdkDragMove) {
    if (!this.isDragging()) return;

    // Hit-test at the cursor (the dragged word itself ignores pointer events while dragging).
    const { x, y } = event.pointerPosition;
    const elementBelow = document.elementFromPoint(x, y);
    const ruleElement = elementBelow?.closest('[data-rule]');
    this.target.set((ruleElement?.getAttribute('data-rule') as TKlankvoetRule | null) ?? undefined);
  }

  async onDragReleased() {
    this.isDragging.set(false);

    const target = this.target();
    if (target === undefined) {
      await this.moveToOrigin();
      return;
    }

    this.isEvaluating.set(true);
    if (target === this.assignment().rule) {
      await this.markCorrect();
    } else {
      await this.markIncorrect();
    }
  }

  async markCorrect() {
    this.isCorrect.set(true);
    AudioEffectPlayer.play('CORRECT');
    await this.overlayFeedback().showCorrect();

    this.next();
  }

  async markIncorrect() {
    this.attempts.update((attempts) => attempts + 1);
    AudioEffectPlayer.play('INCORRECT');
    this.overlayFeedback().showIncorrect();
    await this.moveToOrigin();

    // Let the red flash register before the explanation covers it, or before the user gets to try again.
    await delay(400);
    if (this.attempts() >= MAX_ATTEMPTS) {
      this.showExplanation.set(true);
    } else {
      this.target.set(undefined);
      this.isEvaluating.set(false);
    }
  }

  closeExplanation() {
    this.showExplanation.set(false);
    this.next();
  }

  async moveToOrigin() {
    const duration = 300;
    const element = this.draggable().element.nativeElement;
    element.style.transition = `transform ${duration}ms var(--ease-out-back)`;
    element.style.pointerEvents = 'none';
    this.draggable().setFreeDragPosition({ x: 0, y: 0 });

    await delay(duration);

    element.style.transition = '';
    element.style.pointerEvents = '';
  }

  next() {
    this.attempts.set(0);
    this.isCorrect.set(false);
    this.isEvaluating.set(false);
    this.target.set(undefined);

    if (this.index() === this.assignments().length - 1) {
      this.finished.emit();
    } else {
      this.index.update((i) => i + 1);
    }
  }
}
